/**
 * Prompt Template Service
 * 
 * Handles API interactions with the prompt template system.
 */

export type TemplateScope = 'personal' | 'company' | 'global';

export interface PromptTemplate {
  id: string;
  title: string;
  prompt?: string; // May be hidden for global templates
  description?: string;
  category: string;
  isGlobal: boolean;
  isCompanyTemplate?: boolean;
  scope?: TemplateScope;
  isActive: boolean;
  usageCount: number;
  createdAt: string;
  updatedAt: string;
  creator?: {
    id: string;
    firstName: string;
    lastName: string;
    email: string;
  };
}

export interface CreateTemplateRequest {
  title: string;
  prompt: string;
  description?: string;
  category?: string;
  isGlobal?: boolean;
  isCompanyTemplate?: boolean;
}

export interface UpdateTemplateRequest {
  title?: string;
  prompt?: string;
  description?: string;
  category?: string;
  isActive?: boolean;
}

export interface TemplateSearchOptions {
  search?: string;
  category?: string;
  isActive?: boolean;
  includeGlobal?: boolean;
  limit?: number;
  offset?: number;
  sortBy?: 'title' | 'usageCount' | 'createdAt';
  sortOrder?: 'ASC' | 'DESC';
}

export interface TemplateListResponse {
  templates: PromptTemplate[];
  pagination: {
    total: number;
    limit: number;
    offset: number;
    hasMore: boolean;
  };
}

export interface TemplateSearchResponse {
  suggestions: PromptTemplate[];
}

export interface UsageStatsResponse {
  stats: {
    totalTemplates: number;
    totalUsage: number;
    topTemplates: Array<{
      id: string;
      title: string;
      category: string;
      usageCount: number;
      isGlobal: boolean;
    }>;
  };
}

class PromptTemplateService {
  private baseUrl = `${process.env.REACT_APP_API_BASE_URL || ''}/api/prompt-templates`;
  private adminUrl = `${process.env.REACT_APP_API_BASE_URL || ''}/api/system-admin/prompt-templates`;

  private getAuthHeaders(): HeadersInit {
    const token = localStorage.getItem('riskninja-token');
    return {
      'Content-Type': 'application/json',
      'Authorization': `Bearer ${token}`,
    };
  }

  private async handleResponse<T>(response: Response): Promise<T> {
    if (!response.ok) {
      const errorData = await response.json().catch(() => ({}));
      throw new Error(errorData.error || `HTTP ${response.status}: ${response.statusText}`);
    }
    return response.json();
  }

  /**
   * Get user's accessible templates (personal + global title-only)
   */
  async getUserTemplates(options: TemplateSearchOptions = {}): Promise<TemplateListResponse> {
    const params = new URLSearchParams();
    
    if (options.search) params.append('search', options.search);
    if (options.category) params.append('category', options.category);
    if (options.isActive !== undefined) params.append('isActive', options.isActive.toString());
    if (options.includeGlobal !== undefined) params.append('includeGlobal', options.includeGlobal.toString());
    if (options.limit) params.append('limit', options.limit.toString());
    if (options.offset) params.append('offset', options.offset.toString());
    if (options.sortBy) params.append('sortBy', options.sortBy);
    if (options.sortOrder) params.append('sortOrder', options.sortOrder);

    const url = `${this.baseUrl}?${params}`;
    console.log('Templates API request to:', url);
    console.log('Headers:', this.getAuthHeaders());
    
    const response = await fetch(url, {
      headers: this.getAuthHeaders(),
    });

    console.log('Templates API response status:', response.status);
    console.log('Templates API response headers:', response.headers.get('content-type'));
    
    // Check if response is JSON
    const contentType = response.headers.get('content-type');
    if (!contentType || !contentType.includes('application/json')) {
      const text = await response.text();
      console.error('Non-JSON response received:', text.substring(0, 200));
      throw new Error('Server returned non-JSON response');
    }

    return this.handleResponse<TemplateListResponse>(response);
  }

  /**
   * Search templates for autocomplete
   */
  async searchTemplates(query: string, limit = 10): Promise<TemplateSearchResponse> {
    const params = new URLSearchParams({
      query,
      limit: limit.toString(),
    });

    const response = await fetch(`${this.baseUrl}/search?${params}`, {
      headers: this.getAuthHeaders(),
    });

    return this.handleResponse<TemplateSearchResponse>(response);
  }

  /**
   * Get a specific template by ID
   */
  async getTemplate(templateId: string): Promise<PromptTemplate> {
    const response = await fetch(`${this.baseUrl}/${templateId}`, {
      headers: this.getAuthHeaders(),
    });

    const data = await this.handleResponse<{ template: PromptTemplate }>(response);
    return data.template;
  }

  /**
   * Create a new personal template
   */
  async createTemplate(templateData: CreateTemplateRequest): Promise<PromptTemplate> {
    const response = await fetch(this.baseUrl, {
      method: 'POST',
      headers: this.getAuthHeaders(),
      body: JSON.stringify(templateData),
    });

    const data = await this.handleResponse<{ template: PromptTemplate }>(response);
    return data.template;
  }

  /**
   * Update a template
   */
  async updateTemplate(templateId: string, templateData: UpdateTemplateRequest): Promise<PromptTemplate> {
    const response = await fetch(`${this.baseUrl}/${templateId}`, {
      method: 'PUT',
      headers: this.getAuthHeaders(),
      body: JSON.stringify(templateData),
    });

    const data = await this.handleResponse<{ template: PromptTemplate }>(response);
    return data.template;
  }

  /**
   * Delete a template (soft delete)
   */
  async deleteTemplate(templateId: string): Promise<void> {
    const response = await fetch(`${this.baseUrl}/${templateId}`, {
      method: 'DELETE',
      headers: this.getAuthHeaders(),
    });

    await this.handleResponse<{ message: string }>(response);
  }

  /**
   * Process template with variables
   */
  async processTemplate(templateId: string, variables: Record<string, string> = {}): Promise<{
    originalPrompt: string;
    processedPrompt: string;
    variables: string[];
    remainingVariables: string[];
  }> {
    const response = await fetch(`${this.baseUrl}/${templateId}/process`, {
      method: 'POST',
      headers: this.getAuthHeaders(),
      body: JSON.stringify({ variables }),
    });

    return this.handleResponse(response);
  }

  /**
   * Increment usage count for a template
   */
  async incrementUsage(templateId: string): Promise<void> {
    const response = await fetch(`${this.baseUrl}/${templateId}/use`, {
      method: 'POST',
      headers: this.getAuthHeaders(),
    });

    await this.handleResponse<{ message: string }>(response);
  }

  /**
   * Get template categories
   */
  async getCategories(): Promise<string[]> {
    const response = await fetch(`${this.baseUrl}/categories`, {
      headers: this.getAuthHeaders(),
    });

    const data = await this.handleResponse<{ categories: string[] }>(response);
    return data.categories;
  }

  /**
   * Get usage statistics
   */
  async getUsageStats(): Promise<UsageStatsResponse['stats']> {
    const response = await fetch(`${this.baseUrl}/stats`, {
      headers: this.getAuthHeaders(),
    });

    const data = await this.handleResponse<UsageStatsResponse>(response);
    return data.stats;
  }

  // Admin functions for global templates

  /**
   * Get all global templates (admin only)
   */
  async getGlobalTemplates(options: TemplateSearchOptions = {}): Promise<TemplateListResponse> {
    const params = new URLSearchParams();
    
    if (options.search) params.append('search', options.search);
    if (options.category) params.append('category', options.category);
    if (options.isActive !== undefined) params.append('isActive', options.isActive.toString());
    if (options.limit) params.append('limit', options.limit.toString());
    if (options.offset) params.append('offset', options.offset.toString());
    if (options.sortBy) params.append('sortBy', options.sortBy);
    if (options.sortOrder) params.append('sortOrder', options.sortOrder);

    const response = await fetch(`${this.adminUrl}?${params}`, {
      headers: this.getAuthHeaders(),
    });

    return this.handleResponse<TemplateListResponse>(response);
  }

  /**
   * Create a new global template (admin only)
   */
  async createGlobalTemplate(templateData: CreateTemplateRequest & { companyId?: string }): Promise<PromptTemplate> {
    const response = await fetch(this.adminUrl, {
      method: 'POST',
      headers: this.getAuthHeaders(),
      body: JSON.stringify(templateData),
    });

    const data = await this.handleResponse<{ template: PromptTemplate }>(response);
    return data.template;
  }

  /**
   * Update a global template (admin only)
   */
  async updateGlobalTemplate(templateId: string, templateData: UpdateTemplateRequest): Promise<PromptTemplate> {
    const response = await fetch(`${this.adminUrl}/${templateId}`, {
      method: 'PUT',
      headers: this.getAuthHeaders(),
      body: JSON.stringify(templateData),
    });

    const data = await this.handleResponse<{ template: PromptTemplate }>(response);
    return data.template;
  }

  /**
   * Delete a global template (admin only)
   */
  async deleteGlobalTemplate(templateId: string): Promise<void> {
    const response = await fetch(`${this.adminUrl}/${templateId}`, {
      method: 'DELETE',
      headers: this.getAuthHeaders(),
    });

    await this.handleResponse<{ message: string }>(response);
  }

  /**
   * Get global template usage statistics (admin only)
   */
  async getGlobalTemplateStats(): Promise<UsageStatsResponse['stats']> {
    const response = await fetch(`${this.adminUrl}/stats`, {
      headers: this.getAuthHeaders(),
    });

    const data = await this.handleResponse<UsageStatsResponse>(response);
    return data.stats;
  }

  /**
   * Extract variable placeholders from a prompt
   */
  extractVariables(prompt: string): string[] {
    const variableRegex = /\{([^}]+)\}/g;
    const variables: string[] = [];
    let match;

    while ((match = variableRegex.exec(prompt)) !== null) {
      if (!variables.includes(match[1])) {
        variables.push(match[1]);
      }
    }

    return variables;
  }

  /**
   * Process template variables locally (client-side)
   */
  processTemplateVariables(prompt: string, variables: Record<string, string> = {}): string {
    let processedPrompt = prompt;

    Object.entries(variables).forEach(([key, value]) => {
      const regex = new RegExp(`\\{${key}\\}`, 'g');
      processedPrompt = processedPrompt.replace(regex, value);
    });

    return processedPrompt;
  }
}

export const promptTemplateService = new PromptTemplateService();